import { fetchData, IResponse } from '@umu/umu-fetch';
import { handlePromiseError } from 'common/utils/errorHandler';
import {
    IAccountPlanInfoResponse,
    IAccountPlanPackageListResponse,
    ICreateOrderParams,
    ICreatePayParams,
    IOrderStatusResponse,
    ISaveSubscriptionParams,
    ISubscriptionAmountInfo,
    ISubscriptionCustomerInfoResponse,
    ISubscriptionInfoResponse,
} from '../interface/index';
import API from './api';

/**
 * @description: 获取订阅配置
 * @pay_mode 支付渠道【4：stripe】
 */
export const getSubscriptionConfig = (options?: { pay_mode?: number; site_account?: number }) => {
    const { pay_mode = 4, site_account = 0 } = options || {};
    return fetchData(API.GET_SUBSCRIPTION_CONFIG, { pay_mode, site_account })
        .then((res: IResponse<{ public_key: string }>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'getSubscriptionConfig', 'getSubscriptionConfig fetchData error', {
                pay_mode,
                site_account,
            });
        });
};
/**
 * @description: 获取订阅到期时间
 * @pay_mode
 */
export const getAccountPlan = () => {
    return fetchData(API.GET_ACCOUNT_PLAN, {})
        .then((res: IResponse<IAccountPlanInfoResponse>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'getAccountPlan', 'getAccountPlan fetchData error');
        });
};

/**
 * @description: 获取版本商品列表（复用原有接口）
 * @plan_id int, 账号版本方案 1. 基本版 2. 专业版 3. 商业版 4. 企业版
 * @package_type 套餐类型，默认为1 1:支付套餐 2:订阅套餐
 */
export const getAccountPlanPackageList = (params: { plan_id: number; package_type: number }) => {
    return fetchData(API.GET_ACCOUNT_PLAN_PACKAGE_LIST, params)
        .then((res: IResponse<IAccountPlanPackageListResponse>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'getAccountPlanPackageList', 'getAccountPlanPackageList fetchData error', params);
        });
};
/**
 * @description: 计算订阅价格
 * @product_id string, 产品【premium_plan 专业版账号、business_plan 商业版账号】
 * @package 套餐，套餐【m-1:一个月、y-1:一年】
 * @account_quota number 账号数
 */
export const getSubscriptionAmount = (params: { product_id: string; package: string; account_quota?: number }) => {
    return fetchData(API.GET_SUBSCRIPTION_AMOUNT, params)
        .then((res: IResponse<ISubscriptionAmountInfo>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'getSubscriptionAmount', 'getSubscriptionAmount fetchData error', params);
        });
};
/**
 * @description: 创建订单（复用原有接口）
 */
export const createOrder = (params: ICreateOrderParams) => {
    return fetchData(API.CREATE_ORDER, params, { silent: true })
        .then((res: IResponse<{ is_exist: number; out_trade_no: string }>) => {
            return res;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'createOrder', 'createOrder fetchData error', params);
        });
};
/**
 * @description: 订单支付（复用原有接口）
 */
export const createPay = (params: ICreatePayParams) => {
    return fetchData(API.CREATE_PAY, params, { silent: true })
        .then((res: IResponse<{ pay_mode: number; payment: { client_secret: string } }>) => {
            return res;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'createPay', 'createPay fetchData error', params);
        });
};
/**
 * @description: 查询订单状态（复用原有接口）
 * @out_trade_no string, 交易订单号
 */
export const queryOrder = (params: { out_trade_no: string }) => {
    return fetchData(API.QUERY_ORDER, params)
        .then((res: IResponse<IOrderStatusResponse>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'queryOrder', 'queryOrder fetchData error', params);
        });
};
/**
 * @description: 创建/更新订阅
 */
export const saveSubscription = (params: ISaveSubscriptionParams) => {
    return fetchData(API.SAVE_SUBSCRIPTION, params, { silent: true })
        .then((res: IResponse<IOrderStatusResponse>) => {
            return res;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'saveSubscription', 'saveSubscription fetchData error', params);
        });
};
/**
 * @description: 取消订阅
 */
export const cancelSubscription = () => {
    return fetchData(API.CANCEL_SUBSCRIPTION)
        .then((res: IResponse<{ status: 0 | 1 }>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'cancelSubscription', 'cancelSubscription fetchData error');
        });
};

/**
 * @description: 获取订阅详情
 */
export const getSubscription = () => {
    return fetchData(API.GET_SUBSCRIPTION)
        .then((res: IResponse<ISubscriptionInfoResponse>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'getSubscription', 'getSubscription fetchData error');
        });
};

/**
 * @description: 获取订阅客户详情
 */
export const getSubscriptionCustomer = () => {
    return fetchData(API.GET_SUBSCRIPTION_CUSTOMER)
        .then((res: IResponse<ISubscriptionCustomerInfoResponse>) => {
            return res.data;
        })
        .catch((error: unknown) => {
            handlePromiseError(error, 'getSubscriptionCustomer', 'getSubscriptionCustomer fetchData error');
        });
};
